import ExcelJS from "exceljs";
import JSZip from "jszip";

/**
 * Spreadsheet import parsing for the Project Fairs bulk importer.
 *
 * The importer has to cope with whatever an officer hands it: a hand-rolled CSV, an Excel
 * export, or — the case that motivated this module — a raw Microsoft Forms response export.
 * Nothing here keys off a specific column layout; every mapping is derived by scoring the
 * header text, so a differently-worded sheet carrying the same information still lands.
 */

export type SheetTable = { headers: string[]; rows: Record<string, string>[] };

/** Canonical team roles, matching the server's TEAM_ROLE_ALIASES vocabulary. */
export const ROLE_MENTEE = "Mentee";
export const ROLE_MENTOR = "Mentor";
export const ROLE_TCM = "Committee Member";

export const normalizeHeader = (value: string) =>
  (value || "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, " ")
    .trim();

/**
 * Placeholder answers people type into a required field they can't fill. Treated as empty
 * so we never create a team member literally named "N/A".
 */
const PLACEHOLDER_VALUES = new Set(["n a", "na", "none", "no one", "nobody", "tbd", "n a n a"]);

export const isBlankish = (value: unknown) => {
  const text = String(value ?? "").trim();
  if (!text) return true;
  return PLACEHOLDER_VALUES.has(normalizeHeader(text));
};

const cleanText = (value: unknown) => String(value ?? "").replace(/\s+/g, " ").trim();

// ---------------------------------------------------------------------------
// Reading
// ---------------------------------------------------------------------------

const XML_ENTITIES: Array<[RegExp, string]> = [
  [/&lt;/g, "<"],
  [/&gt;/g, ">"],
  [/&quot;/g, '"'],
  [/&apos;/g, "'"],
];

const decodeXml = (value: string) => {
  let out = String(value);
  XML_ENTITIES.forEach(([pattern, replacement]) => {
    out = out.replace(pattern, replacement);
  });
  out = out.replace(/&#x([0-9a-fA-F]+);/g, (_, hex) => String.fromCodePoint(parseInt(hex, 16)));
  out = out.replace(/&#(\d+);/g, (_, dec) => String.fromCodePoint(Number(dec)));
  // `&amp;` must be decoded last, otherwise "&amp;lt;" would collapse into a real "<".
  return out.replace(/&amp;/g, "&");
};

/** Matches a tag by local name, ignoring any namespace prefix (`x:row` as well as `row`). */
const tag = (name: string) => `(?:[^:\\s>/]+:)?${name}`;

/** "BC12" -> 54. Column letters are base-26 with A = 1. */
const columnIndexFromRef = (ref: string) => {
  const match = /^([A-Z]+)/.exec(ref || "");
  if (!match) return -1;
  let index = 0;
  for (const char of match[1]) {
    index = index * 26 + (char.charCodeAt(0) - 64);
  }
  return index - 1;
};

const readSharedStrings = async (zip: JSZip) => {
  const entry = zip.file(/xl\/sharedStrings\.xml$/i)[0];
  if (!entry) return [];
  const xml = await entry.async("string");
  const strings: string[] = [];
  const siPattern = new RegExp(`<${tag("si")}\\b[^>]*>([\\s\\S]*?)</${tag("si")}>`, "g");
  let si: RegExpExecArray | null;
  while ((si = siPattern.exec(xml))) {
    const tPattern = new RegExp(`<${tag("t")}\\b[^>]*>([\\s\\S]*?)</${tag("t")}>`, "g");
    let text: RegExpExecArray | null;
    let value = "";
    while ((text = tPattern.exec(si[1]))) value += decodeXml(text[1]);
    strings.push(value);
  }
  return strings;
};

/**
 * Namespace-agnostic .xlsx reader used when ExcelJS refuses a workbook.
 *
 * Microsoft Forms exports break ExcelJS twice over: `docProps/core.xml` carries a bare
 * `<lastModifiedBy>` that its CoreXform throws on, and every sheet tag is `x:`-prefixed,
 * which its literal tag matching can't see. Rather than patch a vendored parser we read the
 * XML directly: unzip, resolve shared strings, and walk rows/cells by local tag name. Cell
 * positions come from each cell's `r="A1"` reference, so sparse rows and the bogus
 * `<dimension ref="A1"/>` these files ship don't truncate the read.
 */
export const readXlsxNamespaceAgnostic = async (buffer: ArrayBuffer): Promise<string[][]> => {
  const zip = await JSZip.loadAsync(buffer);
  const shared = await readSharedStrings(zip);
  const sheets = zip.file(/xl\/worksheets\/sheet\d+\.xml$/i).sort((a, b) => a.name.localeCompare(b.name));
  if (!sheets.length) return [];
  const xml = await sheets[0].async("string");

  const rows: string[][] = [];
  const rowPattern = new RegExp(`<${tag("row")}\\b([^>]*?)(?:/>|>([\\s\\S]*?)</${tag("row")}>)`, "g");
  let row: RegExpExecArray | null;
  while ((row = rowPattern.exec(xml))) {
    const cells: string[] = [];
    const cellPattern = new RegExp(`<${tag("c")}\\b([^>]*?)(?:/>|>([\\s\\S]*?)</${tag("c")}>)`, "g");
    let cell: RegExpExecArray | null;
    while ((cell = cellPattern.exec(row[2] || ""))) {
      const attrs = cell[1] || "";
      const inner = cell[2] || "";
      const cellType = /\bt="([^"]+)"/.exec(attrs)?.[1] || "n";

      let value = "";
      if (cellType === "inlineStr") {
        const tPattern = new RegExp(`<${tag("t")}\\b[^>]*>([\\s\\S]*?)</${tag("t")}>`, "g");
        let text: RegExpExecArray | null;
        while ((text = tPattern.exec(inner))) value += decodeXml(text[1]);
      } else {
        const raw = new RegExp(`<${tag("v")}\\b[^>]*>([\\s\\S]*?)</${tag("v")}>`).exec(inner);
        const decoded = raw ? decodeXml(raw[1]) : "";
        value = cellType === "s" ? shared[Number(decoded)] ?? "" : decoded;
      }

      const index = columnIndexFromRef(/\br="([A-Z]+\d+)"/.exec(attrs)?.[1] || "");
      if (index >= 0) cells[index] = value;
      else cells.push(value);
    }
    rows.push(cells);
  }
  return rows;
};

export const parseCsvText = (value: string): SheetTable => {
  const grid: string[][] = [];
  let current: string[] = [];
  let cell = "";
  let inQuotes = false;

  for (let index = 0; index < value.length; index += 1) {
    const char = value[index];
    const next = value[index + 1];

    if (char === '"') {
      if (inQuotes && next === '"') {
        cell += '"';
        index += 1;
      } else {
        inQuotes = !inQuotes;
      }
      continue;
    }

    if ((char === "," || char === "\t") && !inQuotes) {
      current.push(cell);
      cell = "";
      continue;
    }

    if ((char === "\n" || char === "\r") && !inQuotes) {
      if (char === "\r" && next === "\n") index += 1;
      current.push(cell);
      if (current.some((entry) => entry.trim() !== "")) grid.push(current);
      current = [];
      cell = "";
      continue;
    }

    cell += char;
  }

  if (cell !== "" || current.length) {
    current.push(cell);
    if (current.some((entry) => entry.trim() !== "")) grid.push(current);
  }

  return gridToTable(grid);
};

const gridToTable = (grid: unknown[][]): SheetTable => {
  if (!grid.length) return { headers: [], rows: [] };
  const [headerRow, ...dataRows] = grid;
  const headers = (headerRow || []).map((header) => String(header ?? "").trim());
  const rows = dataRows
    .filter((row) => Array.isArray(row) && row.some((entry) => String(entry ?? "").trim() !== ""))
    .map((row) => {
      const record: Record<string, string> = {};
      headers.forEach((header, index) => {
        if (!header) return;
        const value = row[index];
        record[header] = value === undefined || value === null ? "" : String(value).trim();
      });
      return record;
    });
  return { headers: headers.filter(Boolean), rows };
};

/**
 * Reads an uploaded spreadsheet into headers + row records.
 *
 * ExcelJS stays the primary path because it resolves styles and real dates. When it throws
 * we retry with the namespace-agnostic reader rather than surfacing a dead end to the
 * officer, which is what a Microsoft Forms export used to hit.
 */
export const readSpreadsheet = async (file: File): Promise<SheetTable> => {
  const name = file.name.toLowerCase();
  if (name.endsWith(".csv") || name.endsWith(".tsv")) {
    return parseCsvText(await file.text());
  }

  const buffer = await file.arrayBuffer();
  try {
    const workbook = new ExcelJS.Workbook();
    await workbook.xlsx.load(buffer);
    const worksheet = workbook.worksheets[0];
    if (!worksheet) throw new Error("No worksheet");
    // getSheetValues() is 1-based on both axes; drop the leading holes.
    const grid = worksheet.getSheetValues().slice(1).map((row) => (Array.isArray(row) ? row.slice(1) : []));
    const table = gridToTable(grid as unknown[][]);
    if (table.headers.length) return table;
    throw new Error("Empty worksheet");
  } catch (excelError) {
    const grid = await readXlsxNamespaceAgnostic(buffer);
    if (!grid.length) throw excelError;
    return gridToTable(grid);
  }
};

// ---------------------------------------------------------------------------
// Header mapping
// ---------------------------------------------------------------------------

export const IMPORT_FIELDS = [
  { field: "title", label: "Title" },
  { field: "description", label: "Description" },
  { field: "tags", label: "Tags" },
  { field: "chapter", label: "Chapter" },
  { field: "logo", label: "Logo" },
  { field: "thanks", label: "Special thanks" },
] as const;

export type ImportField = (typeof IMPORT_FIELDS)[number]["field"];

/**
 * Columns a survey tool adds about the *respondent* rather than the project. These are only
 * suppressed when the sheet actually looks like a survey export (see `looksLikeFormExport`),
 * so a plain CSV whose project column is simply "Name" still maps correctly.
 */
const RESPONDENT_HEADERS = new Set([
  "id",
  "start time",
  "completion time",
  "email",
  "name",
  "last modified time",
  "response id",
  "submission id",
  "submitted by",
  "respondent",
  "respondent id",
]);

const FORM_EXPORT_MARKERS = ["start time", "completion time", "last modified time", "id", "email"];

/**
 * True when at least three respondent-metadata columns are present — the signature of a
 * survey export. Used to decide whether "Name" means the submitter or the project.
 */
export const looksLikeFormExport = (headers: string[]) => {
  const normalized = new Set(headers.map(normalizeHeader));
  return FORM_EXPORT_MARKERS.filter((marker) => normalized.has(marker)).length >= 3;
};

/** Header text that always describes a person, never the project itself. */
const PERSON_HEADER = /\b(mentee|mentor|committee|tcm|sponsor|triad)\b/;

/**
 * The respondent-identity and ordering columns a survey export carries.
 *
 * These are never mapped to project fields, but they are what makes resubmission detection
 * possible: email identifies *who* submitted, and the timestamps identify *when*, so a
 * mentee who filled the form three times can be collapsed to their final answer.
 */
export type SubmissionMeta = { email: string; submittedAt: string; startedAt: string; id: string };

export const detectSubmissionMeta = (headers: string[]): SubmissionMeta => {
  if (!looksLikeFormExport(headers)) return { email: "", submittedAt: "", startedAt: "", id: "" };
  const find = (test: (normalized: string) => boolean) => headers.find((h) => test(normalizeHeader(h))) || "";
  return {
    email: find((h) => h === "email" || h === "email address" || h === "respondent email"),
    // "Last modified time" beats "Completion time" when both exist: an edited response keeps
    // its original completion stamp, so only the modified stamp reflects the true latest state.
    submittedAt:
      find((h) => h === "last modified time") ||
      find((h) => h === "completion time" || h === "submitted at" || h === "finish time"),
    startedAt: find((h) => h === "start time"),
    id: find((h) => h === "id" || h === "response id" || h === "submission id"),
  };
};

/** Millisecond timestamp for ordering, or null when the value isn't a usable date. */
const timestampOf = (value: unknown) => {
  const raw = String(value ?? "").trim();
  if (!raw) return null;
  const parsed = Date.parse(raw);
  return Number.isNaN(parsed) ? null : parsed;
};

/**
 * Orders two rows from newest to oldest submission.
 *
 * Survey IDs increment, so they are a reliable fallback when a timestamp is missing or
 * unparseable — but only a fallback, because an edited response keeps its original ID while
 * its modified time moves forward.
 */
const bySubmissionRecency = (meta: SubmissionMeta) => (a: Record<string, string>, b: Record<string, string>) => {
  const aTime = timestampOf(a[meta.submittedAt]) ?? timestampOf(a[meta.startedAt]);
  const bTime = timestampOf(b[meta.submittedAt]) ?? timestampOf(b[meta.startedAt]);
  if (aTime !== null && bTime !== null && aTime !== bTime) return bTime - aTime;
  const aId = Number(String(a[meta.id] ?? "").trim());
  const bId = Number(String(b[meta.id] ?? "").trim());
  if (Number.isFinite(aId) && Number.isFinite(bId) && aId !== bId) return bId - aId;
  return 0;
};

export type ResubmissionGroup = {
  /** Who submitted, for display. */
  submitter: string;
  /** The row that will be imported — the most recent one. */
  kept: Record<string, string>;
  /** Superseded rows, newest first. */
  superseded: Record<string, string>[];
  /** Title of the kept row. */
  keptTitle: string;
  /** Titles of the superseded rows, in the same order. */
  supersededTitles: string[];
  /**
   * True when the superseded rows carry a *different* project title. That may be a mentee
   * correcting their title, or it may be one person legitimately submitting two projects —
   * the sheet cannot tell us which, so this is surfaced rather than decided silently.
   */
  titlesDiffer: boolean;
};

/**
 * Collapse repeat submissions from the same person down to their latest response.
 *
 * Mentees resubmit the form — sometimes to fix a title, a description, or to swap a logo —
 * and officers cannot be expected to catch that before handing over the export. Identity is
 * taken from the respondent's email where the survey captured one (stable even if someone
 * types their name differently between attempts) and falls back to their name.
 *
 * A group whose superseded rows share the kept row's title is an unambiguous resubmission.
 * A group with differing titles is still collapsed to the latest — that is the common case
 * and what an officer expects — but it is reported separately so a genuine two-project
 * submitter can be spotted and restored before anything is imported.
 */
export const collapseResubmissions = (
  rows: Record<string, string>[],
  mapping: Record<string, string>,
  meta: SubmissionMeta,
  submitterHeader: string
): { rows: Record<string, string>[]; groups: ResubmissionGroup[] } => {
  const identityOf = (row: Record<string, string>) => {
    const email = cleanText(row[meta.email] || "").toLowerCase();
    if (email) return `email:${email}`;
    const name = normalizeHeader(row[submitterHeader] || "");
    return name ? `name:${name}` : "";
  };

  // No identity column means no safe way to tell a resubmission from two separate projects.
  const identified = rows.some((row) => identityOf(row));
  if (!identified) return { rows, groups: [] };

  const order = new Map(rows.map((row, index) => [row, index]));
  const buckets = new Map<string, Record<string, string>[]>();
  const passthrough: Record<string, string>[] = [];

  rows.forEach((row) => {
    const identity = identityOf(row);
    if (!identity) {
      passthrough.push(row);
      return;
    }
    const bucket = buckets.get(identity);
    if (bucket) bucket.push(row);
    else buckets.set(identity, [row]);
  });

  const kept: Record<string, string>[] = [...passthrough];
  const groups: ResubmissionGroup[] = [];

  buckets.forEach((bucket) => {
    if (bucket.length === 1) {
      kept.push(bucket[0]);
      return;
    }
    const sorted = [...bucket].sort(bySubmissionRecency(meta));
    const [latest, ...superseded] = sorted;
    const titleOf = (row: Record<string, string>) => cleanText(row[mapping.title] || "");
    const keptTitle = titleOf(latest);
    const supersededTitles = superseded.map(titleOf);
    kept.push(latest);
    groups.push({
      submitter: cleanText(latest[submitterHeader] || "") || cleanText(latest[meta.email] || ""),
      kept: latest,
      superseded,
      keptTitle,
      supersededTitles,
      titlesDiffer: supersededTitles.some(
        (title) => normalizeHeader(title) !== normalizeHeader(keptTitle)
      ),
    });
  });

  // Restore the sheet's original ordering so the preview and the import read predictably.
  kept.sort((a, b) => (order.get(a) ?? 0) - (order.get(b) ?? 0));
  return { rows: kept, groups };
};

const FIELD_RULES: Record<ImportField, Array<{ pattern: RegExp; score: number }>> = {
  title: [
    { pattern: /\btitle of (your|the) project\b/, score: 30 },
    { pattern: /\bproject title\b/, score: 28 },
    { pattern: /\bproject name\b/, score: 24 },
    { pattern: /\btitle\b/, score: 18 },
    { pattern: /^project$/, score: 12 },
    { pattern: /^name$/, score: 6 },
  ],
  description: [
    { pattern: /\bdescription of (your|the) project\b/, score: 30 },
    { pattern: /\bproject description\b/, score: 28 },
    { pattern: /\bdescription\b/, score: 20 },
    { pattern: /\bsummary\b/, score: 16 },
    { pattern: /\babstract\b/, score: 14 },
    { pattern: /\belevator pitch\b/, score: 12 },
  ],
  tags: [
    { pattern: /\bproject tags\b/, score: 28 },
    { pattern: /\btags?\b/, score: 20 },
    { pattern: /\bkeywords?\b/, score: 16 },
    { pattern: /\bcategor(y|ies)\b/, score: 14 },
    { pattern: /\btopics?\b/, score: 10 },
  ],
  chapter: [
    { pattern: /\bclear chapter\b/, score: 28 },
    { pattern: /\bchapter\b/, score: 20 },
    { pattern: /\bregion\b/, score: 8 },
  ],
  logo: [
    { pattern: /\bproject logo\b/, score: 28 },
    { pattern: /\blogo\b/, score: 20 },
    { pattern: /\bicon\b/, score: 12 },
    { pattern: /\bimage\b/, score: 10 },
    { pattern: /\bartwork\b/, score: 10 },
  ],
  thanks: [
    { pattern: /\bshout ?outs?\b/, score: 26 },
    { pattern: /\bspecial thanks\b/, score: 26 },
    { pattern: /\bappreciat/, score: 20 },
    { pattern: /\backnowledge?ments?\b/, score: 18 },
    { pattern: /\bthanks?\b/, score: 14 },
  ],
};

const scoreHeader = (field: ImportField, header: string, suppressRespondent: boolean) => {
  const normalized = normalizeHeader(header);
  if (!normalized) return 0;
  if (suppressRespondent && RESPONDENT_HEADERS.has(normalized)) return 0;
  // A column about a specific person can never be the project's own title/description/tags.
  if (PERSON_HEADER.test(normalized) && field !== "thanks") return 0;

  return FIELD_RULES[field].reduce(
    (best, rule) => (rule.pattern.test(normalized) ? Math.max(best, rule.score) : best),
    0,
  );
};

/**
 * Picks the best-scoring column for each importable field. Scoring (rather than exact alias
 * matching) is what lets a full survey question — "What is the title of your project?" —
 * resolve as reliably as a terse "Title" header. Each column is claimed at most once, so a
 * strong match can't be stolen by a weaker field.
 */
export const buildImportMapping = (headers: string[]): Record<string, string> => {
  const suppressRespondent = looksLikeFormExport(headers);
  const candidates: Array<{ field: ImportField; header: string; score: number }> = [];

  IMPORT_FIELDS.forEach(({ field }) => {
    headers.forEach((header) => {
      const score = scoreHeader(field, header, suppressRespondent);
      if (score > 0) candidates.push({ field, header, score });
    });
  });

  candidates.sort((a, b) => b.score - a.score);

  const mapping: Record<string, string> = {};
  IMPORT_FIELDS.forEach(({ field }) => {
    mapping[field] = "";
  });
  const claimed = new Set<string>();

  candidates.forEach(({ field, header, score }) => {
    if (mapping[field] || claimed.has(header)) return;
    void score;
    mapping[field] = header;
    claimed.add(header);
  });

  return mapping;
};

// ---------------------------------------------------------------------------
// People / triad detection
// ---------------------------------------------------------------------------

export type PersonColumn = { role: string; triad: number | null; nameHeader: string; titleHeader: string };
export type PeopleColumns = { members: PersonColumn[]; sponsorName: string; sponsorTitle: string };

const ROLE_PATTERNS: Array<{ pattern: RegExp; role: string }> = [
  { pattern: /\btriad committee member\b|\bcommittee member\b|\btcm\b/, role: ROLE_TCM },
  { pattern: /\bmentee\b/, role: ROLE_MENTEE },
  { pattern: /\bmentor\b/, role: ROLE_MENTOR },
];

/**
 * Whether a header asks for a person's name or their job title. "Provide the Title of
 * Mentee 1." is a *title* column even though `title` is also a project field — role
 * detection runs first, so those headers never reach the project-field scorer.
 */
const partOfPerson = (normalized: string): "name" | "title" | null => {
  if (/\btitle\b|\brole\b|\bposition\b|\bjob\b/.test(normalized)) return "title";
  if (/\bname\b/.test(normalized)) return "name";
  return null;
};

/**
 * Detects repeated person columns and groups them into triads.
 *
 * Column wording varies between exports, so nothing is matched literally: a header is
 * reduced to (role, ordinal, name-or-title) and re-assembled from there. That makes
 * "Provide the Name of Mentee 1.", "Mentee 1 Name" and "Mentee Name 1" all equivalent.
 *
 * The ordinal is the triad number — Mentee 2 / Mentor 2 / TCM 2 form triad 2 together —
 * which matches how the roster is grouped in the project editor. Sheets with no ordinal
 * yield an unassigned member rather than being dropped.
 */
export const detectPeopleColumns = (headers: string[]): PeopleColumns => {
  const slots = new Map<string, PersonColumn>();
  let sponsorName = "";
  let sponsorTitle = "";

  headers.forEach((header) => {
    const normalized = normalizeHeader(header);
    if (!normalized) return;

    const part = partOfPerson(normalized);
    if (!part) return;

    if (/\b(executive )?sponsor\b/.test(normalized)) {
      if (part === "name" && !sponsorName) sponsorName = header;
      if (part === "title" && !sponsorTitle) sponsorTitle = header;
      return;
    }

    const roleMatch = ROLE_PATTERNS.find((entry) => entry.pattern.test(normalized));
    if (!roleMatch) return;

    const ordinal = /(\d+)/.exec(normalized);
    const triad = ordinal ? Number(ordinal[1]) : null;
    const key = `${roleMatch.role}::${triad ?? "none"}`;

    const slot = slots.get(key) || { role: roleMatch.role, triad, nameHeader: "", titleHeader: "" };
    if (part === "name" && !slot.nameHeader) slot.nameHeader = header;
    if (part === "title" && !slot.titleHeader) slot.titleHeader = header;
    slots.set(key, slot);
  });

  const members = [...slots.values()]
    .filter((slot) => slot.nameHeader || slot.titleHeader)
    .sort((a, b) => (a.triad ?? 99) - (b.triad ?? 99));

  return { members, sponsorName, sponsorTitle };
};

// ---------------------------------------------------------------------------
// Row -> project payload
// ---------------------------------------------------------------------------

/**
 * Splits a tag cell into clean tag names.
 *
 * Survey exports separate multi-select answers with ";" and leave the newline that followed
 * each checkbox label embedded in the value. Answers are also commonly prefixed with the
 * question group they came from ("Subject Area: Mobile Apps"); that prefix is a form-design
 * artefact, not part of the tag, and keeping it would fork every existing tag into a second
 * near-duplicate. Only a short leading prefix is stripped, so a tag that genuinely contains
 * a colon mid-phrase survives.
 */
export const normalizeTagList = (value: string) =>
  String(value || "")
    .split(/[;,|\r\n]+/)
    .map((entry) => entry.replace(/\s+/g, " ").trim())
    .filter(Boolean)
    .map((entry) => {
      const stripped = entry.replace(/^[^:]{1,40}:\s*/, "").trim();
      return stripped || entry;
    })
    .filter(Boolean);

/**
 * Builds the lookup used to resolve a spreadsheet tag against the tags that already exist.
 *
 * Tags get renamed over time — a qualifier is added ("Voice" became "Voice (CDV/Telephony)",
 * "HSD" became "Internet (HSD)") — while a survey sent months earlier still offers the old
 * wording. Rather than hard-code an alias table that would rot, every existing tag is
 * indexed under its full name, its name minus any parenthetical qualifier, and the contents
 * of that qualifier. An old label therefore resolves to the current tag on its own.
 *
 * A key that would point at more than one tag is dropped: an ambiguous rename is a judgement
 * call for an officer, not something to guess at during an import.
 */
const buildTagIndex = (existingTags: string[]) => {
  const index = new Map<string, string | null>();
  const add = (key: string, tagName: string) => {
    const normalized = normalizeHeader(key);
    if (!normalized) return;
    if (index.has(normalized) && index.get(normalized) !== tagName) {
      index.set(normalized, null); // ambiguous
      return;
    }
    index.set(normalized, tagName);
  };

  // Exact names are registered first so they always win over a derived alias.
  existingTags.forEach((tagName) => index.set(normalizeHeader(tagName), tagName));
  existingTags.forEach((tagName) => {
    const qualifier = /^(.*?)\s*\(([^)]+)\)\s*$/.exec(tagName);
    if (!qualifier) return;
    const [, base, inside] = qualifier;
    if (!index.has(normalizeHeader(base))) add(base, tagName);
    inside.split("/").forEach((part) => {
      if (!index.has(normalizeHeader(part))) add(part, tagName);
    });
  });

  return index;
};

export type TagResolution = { matched: string[]; unmatched: string[] };

/**
 * Resolves spreadsheet tag names against the existing tag list.
 *
 * Imports never create tags: an unrecognised label is reported back so an officer can add it
 * deliberately (or correct the source data) instead of silently forking the taxonomy with a
 * near-duplicate.
 */
export const resolveTagNames = (names: string[], existingTags: string[]): TagResolution => {
  const index = buildTagIndex(existingTags);
  const matched: string[] = [];
  const unmatched: string[] = [];

  names.forEach((name) => {
    const hit = index.get(normalizeHeader(name));
    if (hit) {
      if (!matched.includes(hit)) matched.push(hit);
    } else if (!unmatched.includes(name)) {
      unmatched.push(name);
    }
  });

  return { matched, unmatched };
};

/**
 * Recovers the original upload filename from a survey attachment URL.
 *
 * Microsoft Forms stores uploads as "<original name>_<submitter name>.<ext>" under a
 * SharePoint path. Keeping the filename lets a later logo archive be matched exactly instead
 * of fuzzily; the submitter's name is personal information and is stripped here so it never
 * reaches storage.
 */
export const logoFileNameFromValue = (value: string, submitterName = "") => {
  const raw = String(value || "").trim();
  if (!raw) return "";
  const withoutQuery = raw.split(/[?#]/)[0];
  const lastSegment = withoutQuery.split("/").pop() || "";
  let fileName = "";
  try {
    fileName = decodeURIComponent(lastSegment);
  } catch (_) {
    fileName = lastSegment;
  }
  if (!fileName) return "";

  const extensionMatch = /\.([A-Za-z0-9]+)$/.exec(fileName);
  const extension = extensionMatch ? extensionMatch[0] : "";
  let stem = extension ? fileName.slice(0, -extension.length) : fileName;

  const submitter = cleanText(submitterName);
  if (submitter) {
    const suffix = `_${submitter}`;
    if (stem.toLowerCase().endsWith(suffix.toLowerCase())) {
      stem = stem.slice(0, -suffix.length);
    }
  }

  return `${stem.trim() || "logo"}${extension}`;
};

export type ImportedProject = {
  title: string;
  description: string;
  tags: string[];
  chapter: string;
  thanks: string;
  logo_file_name: string;
  team: Array<{ name: string; role: string; title: string; triad?: number }>;
  exec_sponsor: Array<{ name: string; title?: string }>;
};

/** Flattens one spreadsheet row into the project payload the import endpoint accepts. */
export const buildImportedProject = (
  row: Record<string, string>,
  mapping: Record<string, string>,
  people: PeopleColumns,
  submitterHeader = "",
): ImportedProject => {
  const valueOf = (header: string) => (header ? cleanText(row[header]) : "");

  const team = people.members
    .map((slot) => {
      const name = valueOf(slot.nameHeader);
      if (isBlankish(name)) return null;
      const title = valueOf(slot.titleHeader);
      return {
        name,
        role: slot.role,
        title: isBlankish(title) ? "" : title,
        ...(slot.triad ? { triad: slot.triad } : {}),
      };
    })
    .filter((member): member is NonNullable<typeof member> => member !== null);

  const sponsorName = valueOf(people.sponsorName);
  const sponsorTitle = valueOf(people.sponsorTitle);
  const exec_sponsor = isBlankish(sponsorName)
    ? []
    : [{ name: sponsorName, ...(isBlankish(sponsorTitle) ? {} : { title: sponsorTitle }) }];

  const thanks = valueOf(mapping.thanks);

  return {
    title: valueOf(mapping.title),
    description: valueOf(mapping.description),
    tags: normalizeTagList(row[mapping.tags] || ""),
    chapter: valueOf(mapping.chapter),
    thanks: isBlankish(thanks) ? "" : thanks,
    logo_file_name: logoFileNameFromValue(row[mapping.logo] || "", row[submitterHeader] || ""),
    team,
    exec_sponsor,
  };
};

/** The respondent-name column, used only to strip personal information from logo filenames. */
export const findSubmitterHeader = (headers: string[]) => {
  if (!looksLikeFormExport(headers)) return "";
  return headers.find((header) => normalizeHeader(header) === "name") || "";
};
